import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../api';
import {
  PageHeader, Loading, ErrorBanner, ui,
} from '../components/adminUi';
import Icon from '../components/Icon';

// Narx sozlamalari — hammasi `pricing_rules` jadvalidan (deploy KERAK EMAS,
// saqlash bilan darhol kuchga kiradi: backend narx keshini tozalaydi).
//
// Eski "har kategoriya uchun foiz komissiya" OLIB TASHLANDI: komissiya endi
// har kelishuv uchun BITTA summa (foiz emas), kategoriyaga bog'liq emas.
const FIELDS = [
  {
    kind: 'deal_fee',
    label: 'Kelishuv komissiyasi',
    hint: 'Mijoz taklifni qabul qilganda ustadan yechiladigan summa.',
    unit: "so'm",
  },
  {
    kind: 'deal_fee_payer',
    label: "Komissiyani kim to'laydi",
    hint: "Mijoz tanlansa, hamyoni yo'qligi uchun summa qarzdorlik bo'lib qayd etiladi.",
    type: 'select',
    options: [{ v: 0, label: 'Usta' }, { v: 1, label: 'Mijoz' }],
  },
  {
    kind: 'prime_price',
    label: 'Prime obuna narxi',
    hint: 'Prime davomida kelishuvlardan komissiya olinmaydi.',
    unit: "so'm",
  },
  {
    kind: 'prime_days',
    label: 'Prime muddati',
    hint: 'Bir marta to‘lovda necha kun amal qiladi.',
    unit: 'kun',
  },
  {
    kind: 'signup_bonus',
    label: "Ro'yxatdan o'tish bonusi",
    hint: "Yangi usta ro'yxatdan o'tganda hisobiga tushadigan kredit.",
    unit: "so'm",
  },
  {
    kind: 'max_offers',
    label: "Bitta e'longa maksimal taklif",
    hint: 'Mijoz shu sondan ortiq taklif olmaydi.',
    unit: 'ta',
  },
];

export default function CommissionPage() {
  const { auth } = useAuth();

  const [rules, setRules] = useState([]);
  const [values, setValues] = useState({});   // kind -> string
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [savingKind, setSavingKind] = useState(null);
  const [msg, setMsg] = useState({});          // kind -> {tone, text}

  const load = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const res = await apiFetch('/api/admin/pricing', auth.token);
      if (!res.ok) throw new Error();
      const d = await res.json();
      const list = Array.isArray(d.rules) ? d.rules : [];
      setRules(list);
      const init = {};
      FIELDS.forEach((f) => {
        const r = globalRule(list, f.kind);
        init[f.kind] = r ? String(Number(r.amount)) : '';
      });
      setValues(init);
    } catch {
      setRules([]);
      setFetchError('Sozlamalarni yuklashda xatolik. Sahifani yangilang.');
    } finally {
      setLoading(false);
    }
  }, [auth.token]);

  useEffect(() => { load(); }, [load]);

  const save = async (field) => {
    const raw = values[field.kind];
    const n = Number(raw);
    setMsg((m) => ({ ...m, [field.kind]: null }));
    if (raw === '' || !Number.isFinite(n) || n < 0) {
      setMsg((m) => ({ ...m, [field.kind]: { tone: 'red', text: "Qiymat noto'g'ri" } }));
      return;
    }
    setSavingKind(field.kind);
    try {
      const existing = globalRule(rules, field.kind);
      // Qoida bo'lmasa (o'chirilgan bo'lsa) — yangisini yaratamiz.
      const res = existing
        ? await apiFetch(`/api/admin/pricing/${existing.id}`, auth.token, {
            method: 'PATCH',
            body: JSON.stringify({ amount: n }),
          })
        : await apiFetch('/api/admin/pricing', auth.token, {
            method: 'POST',
            body: JSON.stringify({ kind: field.kind, amount: n, scopeType: 'global' }),
          });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setMsg((m) => ({ ...m, [field.kind]: { tone: 'green', text: 'Saqlandi' } }));
        load();
      } else {
        setMsg((m) => ({ ...m, [field.kind]: { tone: 'red', text: d.message || 'Xatolik' } }));
      }
    } catch {
      setMsg((m) => ({ ...m, [field.kind]: { tone: 'red', text: 'Tarmoq xatosi' } }));
    } finally {
      setSavingKind(null);
    }
  };

  // Global bo'lmagan (mavsumiy / kategoriya bo'yicha) qoidalar — bo'lsa ogohlantiramiz,
  // chunki ular bu yerdagi qiymatdan USTUN turadi.
  const extras = rules.filter((r) => r.scopeType !== 'global' && r.isActive);

  return (
    <AdminLayout>
      <PageHeader
        title="Narx sozlamalari"
        subtitle="Komissiya, Prime obuna va boshqa summalar — o'zgarish darhol kuchga kiradi"
      >
        <button style={ui.pagerBtn} onClick={load}><Icon name="refresh" size={14} style={{ marginRight: '6px' }} />Yangilash</button>
      </PageHeader>

      <ErrorBanner>{fetchError}</ErrorBanner>

      {extras.length > 0 && (
        <div style={ui.infoBanner}>
          <Icon name="alert" size={14} style={{ marginRight: '6px' }} />{extras.length} ta maxsus qoida bor (kategoriya/hudud bo'yicha) — ular quyidagi
          umumiy qiymatdan ustun turadi:{' '}
          {extras.map((r) => `${r.kind} = ${r.amount}`).join(', ')}
        </div>
      )}

      {loading ? (
        <Loading />
      ) : (
        <div style={ui.tableWrap}>
          <table style={ui.table}>
            <thead>
              <tr>
                <th style={ui.th}>Sozlama</th>
                <th style={{ ...ui.th, width: '260px' }}>Qiymat</th>
                <th style={{ ...ui.th, width: '150px', textAlign: 'right' }}>Amal</th>
              </tr>
            </thead>
            <tbody>
              {FIELDS.map((f) => {
                const m = msg[f.kind];
                const busy = savingKind === f.kind;
                return (
                  <tr key={f.kind} style={ui.tr}>
                    <td style={ui.td}>
                      <div style={s.name}>{f.label}</div>
                      <div style={s.hint}>{f.hint}</div>
                    </td>
                    <td style={ui.td}>
                      <div style={s.inputWrap}>
                        {f.type === 'select' ? (
                          <select
                            style={{ ...ui.select, width: '150px' }}
                            value={values[f.kind] ?? ''}
                            onChange={(e) => setValues((v) => ({ ...v, [f.kind]: e.target.value }))}
                            disabled={busy}
                          >
                            {f.options.map((o) => (
                              <option key={o.v} value={String(o.v)}>{o.label}</option>
                            ))}
                          </select>
                        ) : (
                          <>
                            <input
                              type="number"
                              min="0"
                              style={{ ...ui.input, width: '150px' }}
                              value={values[f.kind] ?? ''}
                              onChange={(e) => setValues((v) => ({ ...v, [f.kind]: e.target.value }))}
                              disabled={busy}
                              placeholder="0"
                            />
                            <span style={s.unit}>{f.unit}</span>
                          </>
                        )}
                        {m && <span style={{ ...s.msg, ...toneStyle(m.tone) }}>{m.text}</span>}
                      </div>
                      {/* Qator balandligi bir xil bo'lishi uchun izoh joyi har doim band */}
                      <div style={s.preview}>
                        {f.unit === "so'm" && Number(values[f.kind]) > 0 ? `${money(values[f.kind])} so'm` : ''}
                      </div>
                    </td>
                    <td style={{ ...ui.td, textAlign: 'right' }}>
                      <button
                        style={{ ...ui.saveBtn, padding: '8px 20px', ...(busy ? ui.btnDisabled : {}) }}
                        onClick={() => save(f)}
                        disabled={busy}
                      >
                        {busy ? '...' : 'Saqlash'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

function globalRule(list, kind) {
  return list.find((r) => r.kind === kind && r.scopeType === 'global' && r.isActive) || null;
}

function toneStyle(tone) {
  if (tone === 'green') return { color: 'var(--color-success)' };
  if (tone === 'red') return { color: 'var(--color-danger)' };
  return { color: 'var(--color-text-muted)' };
}

// Bo'sh joyli format: 1 000 000
function money(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

const s = {
  name: { fontWeight: '600', color: 'var(--color-text)', fontSize: '14px' },
  hint: { fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '3px', maxWidth: '420px' },
  inputWrap: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  unit: { color: 'var(--color-text-muted)', fontSize: '14px' },
  msg: { fontSize: '12px', fontWeight: '600' },
  preview: { fontSize: '12px', lineHeight: '16px', minHeight: '16px', color: 'var(--color-text-muted)', marginTop: '4px' },
};
